import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TokenUsageSummary, LLMModelInfo, VideoUsageSummary, VideoModel, ImageUsageSummary, ImageModel, ProjectServiceUsageSummary } from '../types';
import { getProjectTokenUsage, getProjectVideoUsage, getProjectImageUsage, getProjectServiceUsage, getProject, listLLMProviders, listActiveVideoModels, listActiveImageModels } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import EmptyState from '../components/EmptyState';
import { useTranslation } from 'react-i18next';
import { toI18nKeySegment } from '../utils/i18nKey';

// STEP_LABELS moved to i18n: profile.operationTypes.*

function formatNumber(n: number): string {
  return n.toLocaleString('zh-CN');
}

function formatCost(cost: number): string {
  if (cost === 0) return '-';
  if (cost < 0.01) return `¥${cost.toFixed(4)}`;
  return `¥${cost.toFixed(2)}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m${s}s` : `${m}m`;
}

const ProjectUsage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('profile');
  const [projectName, setProjectName] = useState('');
  const [data, setData] = useState<TokenUsageSummary[]>([]);
  const [videoData, setVideoData] = useState<VideoUsageSummary[]>([]);
  const [imageData, setImageData] = useState<ImageUsageSummary[]>([]);
  const [serviceData, setServiceData] = useState<ProjectServiceUsageSummary[]>([]);
  const [modelNames, setModelNames] = useState<Record<string, string>>({});
  const [videoModelNames, setVideoModelNames] = useState<Record<string, string>>({});
  const [imageModelNames, setImageModelNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      getProject(id).then(p => setProjectName(p.name)),
      // 后端 Go nil slice 会序列化为 null（旧版后端 service-usage 已实际发生过），
      // 渲染处对数组调 .reduce/.map，必须兜底为 []
      getProjectTokenUsage(id).then(d => setData(d || [])),
      getProjectVideoUsage(id).then(d => setVideoData(d || [])),
      getProjectImageUsage(id).then(d => setImageData(d || [])),
      getProjectServiceUsage(id).then(d => setServiceData(d || [])),
      listLLMProviders().then(providers => {
        const map: Record<string, string> = {};
        for (const p of providers || []) {
          try {
            const models: LLMModelInfo[] = JSON.parse(p.models || '[]');
            for (const m of models) {
              if (m.displayName) map[m.name] = m.displayName;
            }
          } catch {}
        }
        setModelNames(map);
      }),
      listActiveVideoModels().then((models: VideoModel[]) => {
        const map: Record<string, string> = {};
        for (const m of models || []) {
          if (m.displayName) map[m.model] = m.displayName;
        }
        setVideoModelNames(map);
      }).catch(() => {}),
      listActiveImageModels().then((models: ImageModel[]) => {
        const map: Record<string, string> = {};
        for (const m of models || []) {
          if (m.displayName) map[m.model] = m.displayName;
        }
        setImageModelNames(map);
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [id]);

  const totals = data.reduce(
    (acc, row) => ({
      promptTokens: acc.promptTokens + row.promptTokens,
      completionTokens: acc.completionTokens + row.completionTokens,
      totalTokens: acc.totalTokens + row.totalTokens,
      cost: acc.cost + row.cost,
      callCount: acc.callCount + row.callCount,
    }),
    { promptTokens: 0, completionTokens: 0, totalTokens: 0, cost: 0, callCount: 0 }
  );

  const videoTotals = videoData.reduce(
    (acc, row) => ({
      count: acc.count + row.count,
      totalDuration: acc.totalDuration + row.totalDuration,
      totalCost: acc.totalCost + row.totalCost,
    }),
    { count: 0, totalDuration: 0, totalCost: 0 }
  );

  const imageTotals = imageData.reduce(
    (acc, row) => ({
      count: acc.count + row.count,
      totalCost: acc.totalCost + row.totalCost,
    }),
    { count: 0, totalCost: 0 }
  );

  const serviceTotals = serviceData.reduce(
    (acc, row) => ({
      callCount: acc.callCount + row.callCount,
      totalCost: acc.totalCost + row.totalCost,
    }),
    { callCount: 0, totalCost: 0 }
  );

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('projectUsage.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <Breadcrumb items={[
        { label: t('projectUsage.breadcrumbProjects'), to: '/projects' },
        { label: projectName || t('projectUsage.breadcrumbProject'), to: `/projects/${id}` },
        { label: t('projectUsage.breadcrumbTitle') },
      ]} />

      <div className="page-header">
        <h1 className="page-title">{t('projectUsage.pageTitle')}</h1>
      </div>

      {/* LLM Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.llmCalls')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{formatNumber(totals.callCount)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.inputToken')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{formatNumber(totals.promptTokens)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.outputToken')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{formatNumber(totals.completionTokens)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.totalToken')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{formatNumber(totals.totalTokens)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.llmCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{formatCost(totals.cost)}</div>
        </div>
      </div>
      {/* Image + Video Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.imageCount')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{formatNumber(imageTotals.count)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.imageCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{formatCost(imageTotals.totalCost)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.videoCount')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{formatNumber(videoTotals.count)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.videoDuration')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>{formatDuration(videoTotals.totalDuration)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.videoCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{formatCost(videoTotals.totalCost)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.serviceCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{formatCost(serviceTotals.totalCost)}</div>
        </div>
      </div>
      {/* Total cost summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.llmCost')}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(totals.cost)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.imageCost')}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(imageTotals.totalCost)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.videoCost')}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(videoTotals.totalCost)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.serviceCost')}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(serviceTotals.totalCost)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '1.25rem', background: 'var(--bg-2, var(--surface-2, #f9fafb))' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('projectUsage.totalCost')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--danger, #e74c3c)' }}>{formatCost(totals.cost + imageTotals.totalCost + videoTotals.totalCost + serviceTotals.totalCost)}</div>
        </div>
      </div>

      {/* LLM Detail table */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>{t('projectUsage.llmDetailTitle')}</h3>
        {data.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('projectUsage.thStep')}</th>
                  <th style={thStyle}>{t('projectUsage.thModel')}</th>
                  <th style={thStyle}>{t('projectUsage.thProvider')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thCalls')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thInputToken')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thOutputToken')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thTotalToken')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thCost')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>
                      <span className="status-badge status-uploading" style={{ fontSize: '0.75rem' }}>
                        {t('operationTypes.' + toI18nKeySegment(row.step), { defaultValue: row.step })}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-2)', fontSize: '0.8rem' }}>{modelNames[row.model] || row.model}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-3)', fontSize: '0.8rem' }}>{row.provider || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.callCount)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.promptTokens)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.completionTokens)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>{formatNumber(row.totalTokens)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(row.cost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td colSpan={3} style={{ ...tdStyle, fontWeight: 600 }}>{t('projectUsage.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatNumber(totals.callCount)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatNumber(totals.promptTokens)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatNumber(totals.completionTokens)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatNumber(totals.totalTokens)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(totals.cost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#128202;" description={t("projectUsage.emptyLLM")} />
        )}
      </div>

      {/* Image Usage table */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>{t('projectUsage.imageDetailTitle')}</h3>
        {imageData.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('projectUsage.thPlatform')}</th>
                  <th style={thStyle}>{t('projectUsage.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thGenCount')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thCost')}</th>
                </tr>
              </thead>
              <tbody>
                {imageData.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>{row.platform}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-2)', fontSize: '0.8rem' }}>{imageModelNames[row.model] || row.model}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.count)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(row.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td colSpan={2} style={{ ...tdStyle, fontWeight: 600 }}>{t('projectUsage.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatNumber(imageTotals.count)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(imageTotals.totalCost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#128248;" description={t("projectUsage.emptyImage")} />
        )}
      </div>

      {/* Video Usage table */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>{t('projectUsage.videoDetailTitle')}</h3>
        {videoData.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('projectUsage.thPlatform')}</th>
                  <th style={thStyle}>{t('projectUsage.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thGenCount')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thDuration')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thCost')}</th>
                </tr>
              </thead>
              <tbody>
                {videoData.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>{row.platform}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-2)', fontSize: '0.8rem' }}>{videoModelNames[row.model] || row.model}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.count)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatDuration(row.totalDuration)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(row.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td colSpan={2} style={{ ...tdStyle, fontWeight: 600 }}>{t('projectUsage.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatNumber(videoTotals.count)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatDuration(videoTotals.totalDuration)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(videoTotals.totalCost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#127916;" description={t("projectUsage.emptyVideo")} />
        )}
      </div>

      {/* Service Usage table */}
      <div className="card">
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>{t('projectUsage.serviceDetailTitle')}</h3>
        {serviceData.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('projectUsage.thStep')}</th>
                  <th style={thStyle}>{t('projectUsage.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thCalls')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('projectUsage.thCost')}</th>
                </tr>
              </thead>
              <tbody>
                {serviceData.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>
                      <span className="status-badge status-uploading" style={{ fontSize: '0.75rem' }}>
                        {t('operationTypes.' + toI18nKeySegment(row.operationName), { defaultValue: row.operationName })}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-2)', fontSize: '0.8rem' }}>{modelNames[row.model] || row.model || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.callCount)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(row.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td colSpan={2} style={{ ...tdStyle, fontWeight: 600 }}>{t('projectUsage.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatNumber(serviceTotals.callCount)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(serviceTotals.totalCost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#128179;" description={t("projectUsage.emptyService")} />
        )}
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontWeight: 600,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  whiteSpace: 'nowrap',
};

export default ProjectUsage;
